import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { PartyPopper, Plus, QrCode, Copy, Share2, Pencil, Trash2, Lock, Unlock, Receipt, X } from 'lucide-react';
import { API } from '../../utils/api';
import { useAuthContext } from '../../context/AuthContext';

const EMPTY_FORM = {
  name: '', description: '', amountType: 'open', fixed_amount: '',
  default_message: '', end_date: '', line_items: [{ description: '', amount: '' }],
};

export function PersonalEvents() {
  const { t } = useTranslation();
  const { headers } = useAuthContext();
  const [events, setEvents] = useState([]);
  const [usage, setUsage] = useState({ active: 0, max_events: 0, max_event_line_items: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [qrEvent, setQrEvent] = useState(null);
  const [paymentsEvent, setPaymentsEvent] = useState(null);
  const [payments, setPayments] = useState([]);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/personal/events`, { headers });
      setEvents(res.data.items || []);
      setUsage(res.data.usage || { active: 0, max_events: 0, max_event_line_items: 0 });
    } catch (e) {
      console.error('Failed to fetch personal events:', e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const atCap = usage.max_events !== -1 && usage.active >= usage.max_events;
  const lineItemsAllowed = usage.max_event_line_items > 0;
  const maxItems = Math.min(usage.max_event_line_items || 0, 4);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      name: ev.name || '',
      description: ev.description || '',
      amountType: ev.payment_mode === 'LINE_ITEMS' ? 'items' : (ev.fixed_amount ? 'fixed' : 'open'),
      fixed_amount: ev.fixed_amount || '',
      default_message: ev.default_message || '',
      end_date: ev.end_date ? ev.end_date.slice(0, 10) : '',
      line_items: (ev.line_items && ev.line_items.length > 0)
        ? ev.line_items.map(li => ({ description: li.description, amount: li.amount }))
        : [{ description: '', amount: '' }],
    });
    setDialogOpen(true);
  };

  const buildBody = () => {
    const body = {
      name: form.name.trim(),
      description: form.description || null,
      default_message: form.default_message || null,
      end_date: form.end_date || null,
    };
    if (form.amountType === 'items') {
      body.payment_mode = 'LINE_ITEMS';
      body.line_items = form.line_items
        .filter(li => li.description.trim() && parseFloat(li.amount) > 0)
        .map(li => ({ description: li.description.trim(), amount: parseFloat(li.amount) }));
    } else {
      body.payment_mode = 'AMOUNT';
      body.fixed_amount = form.amountType === 'fixed' ? (parseFloat(form.fixed_amount) || null) : null;
      body.line_items = null;
    }
    return body;
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const body = buildBody();
      if (editingId) {
        const res = await axios.patch(`${API}/personal/events/${editingId}`, body, { headers });
        setEvents(prev => prev.map(x => x.id === editingId
          ? { ...res.data, total_received: x.total_received, payment_count: x.payment_count } : x));
        toast.success(t('tools.personalEvents.updated'));
      } else {
        const res = await axios.post(`${API}/personal/events`, body, { headers });
        setEvents(prev => [{ ...res.data, total_received: 0, payment_count: 0 }, ...prev]);
        if (res.data.usage) setUsage(res.data.usage);
        toast.success(t('tools.personalEvents.created'));
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t(editingId ? 'tools.personalEvents.updateFailed' : 'tools.personalEvents.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (ev) => {
    try {
      const res = await axios.patch(`${API}/personal/events/${ev.id}`,
        { status: ev.status === 'ACTIVE' ? 'CLOSED' : 'ACTIVE' }, { headers });
      setEvents(prev => prev.map(x => x.id === ev.id
        ? { ...res.data, total_received: x.total_received, payment_count: x.payment_count } : x));
      setUsage(u => ({ ...u, active: Math.max(0, u.active + (res.data.status === 'ACTIVE' ? 1 : -1)) }));
      toast.success(t('tools.personalEvents.updated'));
      fetchEvents();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('tools.personalEvents.updateFailed'));
    }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(t('tools.personalEvents.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/personal/events/${ev.id}`, { headers });
      setEvents(prev => prev.filter(x => x.id !== ev.id));
      if (ev.status === 'ACTIVE') setUsage(u => ({ ...u, active: Math.max(0, u.active - 1) }));
      toast.success(t('tools.personalEvents.deleted'));
      fetchEvents();
    } catch (e) {
      toast.error(t('tools.personalEvents.updateFailed'));
    }
  };

  const copyLink = (ev) => {
    navigator.clipboard.writeText(ev.payment_url);
    toast.success(t('tools.personalEvents.linkCopied'));
  };

  const shareLink = async (ev) => {
    if (navigator.share) {
      try { await navigator.share({ title: ev.name, url: ev.payment_url }); } catch (_) { /* cancelled */ }
    } else {
      copyLink(ev);
    }
  };

  const openPayments = async (ev) => {
    setPaymentsEvent(ev);
    setPayments([]);
    try {
      const res = await axios.get(`${API}/personal/events/${ev.id}/payments`, { headers });
      setPayments(res.data.items || []);
    } catch (e) {
      console.error('Failed to fetch payments:', e);
    }
  };

  const setItem = (idx, key, val) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [key]: val };
    setForm({ ...form, line_items: items });
  };

  if (loading) {
    return <div className="animate-pulse bg-gray-100 rounded-lg h-24" data-testid="personal-events-loading" />;
  }

  return (
    <section data-testid="personal-events-section">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <PartyPopper className="w-8 h-8 text-teal-500" />
          <h2 className="text-2xl font-bold text-gray-800">{t('tools.personalEvents.title')}</h2>
        </div>
        <Button onClick={openCreate} disabled={atCap} size="sm" data-testid="create-event-btn">
          <Plus className="w-4 h-4 mr-1" /> {t('tools.personalEvents.create')}
        </Button>
      </div>
      <p className="text-sm text-gray-500 mb-2">{t('tools.personalEvents.subtitle')}</p>
      <div className="mb-4">
        <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200" data-testid="events-usage-badge">
          {usage.max_events === -1
            ? t('tools.personalEvents.usageUnlimited', { active: usage.active })
            : t('tools.personalEvents.usage', { active: usage.active, max: usage.max_events })}
        </Badge>
        {atCap && (
          <p className="text-xs text-amber-600 mt-1" data-testid="events-limit-warning">
            {t('tools.personalEvents.limitReached', { max: usage.max_events })}
          </p>
        )}
      </div>

      {events.length === 0 && (
        <Card className="border-dashed" data-testid="events-empty-state">
          <CardContent className="p-8 text-center text-gray-400 text-sm">
            {t('tools.personalEvents.empty')}
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {events.map(ev => (
          <Card key={ev.id} className={ev.status !== 'ACTIVE' ? 'opacity-70' : ''} data-testid={`event-card-${ev.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 truncate">{ev.name}</span>
                    <Badge variant={ev.status === 'ACTIVE' ? 'default' : 'secondary'}>
                      {ev.status === 'ACTIVE' ? t('tools.personalEvents.active') : t('tools.personalEvents.closed')}
                    </Badge>
                  </div>
                  {ev.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{ev.description}</p>}
                  <p className="text-xs text-gray-400 mt-0.5">{ev.display_paymig_id}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-bold text-teal-600" data-testid={`event-received-${ev.id}`}>
                    {ev.total_received} SEK
                  </div>
                  <div className="text-xs text-gray-400">
                    {ev.payment_count} {t('tools.personalEvents.payments').toLowerCase()}
                  </div>
                </div>
              </div>

              {ev.payment_mode === 'LINE_ITEMS' && ev.line_items && (
                <div className="mt-2 text-xs text-gray-500">
                  {ev.line_items.map((li, i) => (
                    <span key={i} className="inline-block bg-gray-100 rounded px-2 py-0.5 mr-1 mb-1">
                      {li.description}: {li.amount} SEK
                    </span>
                  ))}
                </div>
              )}
              {ev.payment_mode === 'AMOUNT' && ev.fixed_amount && (
                <div className="mt-1 text-xs text-gray-500">{t('tools.personalEvents.fixedAmount')}: {ev.fixed_amount} SEK</div>
              )}

              <div className="flex flex-wrap gap-1.5 mt-3">
                <Button variant="outline" size="sm" onClick={() => setQrEvent(ev)} data-testid={`event-qr-${ev.id}`}>
                  <QrCode className="w-4 h-4 mr-1" /> {t('tools.personalEvents.showQr')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => copyLink(ev)} data-testid={`event-copy-${ev.id}`}>
                  <Copy className="w-4 h-4 mr-1" /> {t('tools.personalEvents.copyLink')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => shareLink(ev)} data-testid={`event-share-${ev.id}`}>
                  <Share2 className="w-4 h-4 mr-1" /> {t('tools.personalEvents.share')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => openPayments(ev)} data-testid={`event-payments-${ev.id}`}>
                  <Receipt className="w-4 h-4 mr-1" /> {t('tools.personalEvents.payments')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}>
                  <Pencil className="w-4 h-4 mr-1" /> {t('tools.personalEvents.edit')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => toggleStatus(ev)} data-testid={`event-toggle-${ev.id}`}>
                  {ev.status === 'ACTIVE'
                    ? <><Lock className="w-4 h-4 mr-1" /> {t('tools.personalEvents.close')}</>
                    : <><Unlock className="w-4 h-4 mr-1" /> {t('tools.personalEvents.reopen')}</>}
                </Button>
                <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(ev)} data-testid={`event-delete-${ev.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="event-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? t('tools.personalEvents.edit') : t('tools.personalEvents.create')}</DialogTitle>
            <DialogDescription>{t('tools.personalEvents.subtitle')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('tools.personalEvents.name')}</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder={t('tools.personalEvents.namePlaceholder')} data-testid="event-name-input" />
            </div>
            <div>
              <Label>{t('tools.personalEvents.description')}</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                data-testid="event-description-input" />
            </div>
            <div>
              <Label>{t('tools.personalEvents.amountMode')}</Label>
              <Select value={form.amountType} onValueChange={v => setForm({ ...form, amountType: v })}>
                <SelectTrigger data-testid="event-amount-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">{t('tools.personalEvents.openAmount')}</SelectItem>
                  <SelectItem value="fixed">{t('tools.personalEvents.fixedAmount')}</SelectItem>
                  {lineItemsAllowed && <SelectItem value="items">{t('tools.personalEvents.lineItems')}</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {form.amountType === 'fixed' && (
              <div>
                <Label>{t('tools.personalEvents.fixedAmount')} (SEK)</Label>
                <Input type="number" value={form.fixed_amount}
                  onChange={e => setForm({ ...form, fixed_amount: e.target.value })} data-testid="event-fixed-amount-input" />
              </div>
            )}
            {form.amountType === 'items' && (
              <div className="space-y-2">
                <Label>{t('tools.personalEvents.lineItems')} <span className="text-xs text-gray-400">({t('tools.personalEvents.lineItemsHint', { max: maxItems })})</span></Label>
                {form.line_items.map((li, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input className="flex-1" placeholder={t('tools.personalEvents.itemDescription')}
                      value={li.description} onChange={e => setItem(idx, 'description', e.target.value)}
                      data-testid={`event-item-desc-${idx}`} />
                    <Input className="w-24" type="number" placeholder={t('tools.personalEvents.itemAmount')}
                      value={li.amount} onChange={e => setItem(idx, 'amount', e.target.value)}
                      data-testid={`event-item-amount-${idx}`} />
                    {form.line_items.length > 1 && (
                      <button type="button" className="text-gray-400 hover:text-red-500"
                        onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) })}
                        data-testid={`event-item-remove-${idx}`}>
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {form.line_items.length < maxItems && (
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setForm({ ...form, line_items: [...form.line_items, { description: '', amount: '' }] })}
                    data-testid="event-add-item-btn">
                    <Plus className="w-4 h-4 mr-1" /> {t('tools.personalEvents.addItem')}
                  </Button>
                )}
              </div>
            )}
            <div>
              <Label>{t('tools.personalEvents.defaultMessage')}</Label>
              <Input value={form.default_message} onChange={e => setForm({ ...form, default_message: e.target.value })}
                maxLength={50} data-testid="event-default-message-input" />
            </div>
            <div>
              <Label>{t('tools.personalEvents.endDate')}</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                data-testid="event-end-date-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={handleSubmit} disabled={saving || !form.name.trim()} data-testid="event-submit-btn">
                {editingId ? t('tools.personalEvents.saveBtn') : t('tools.personalEvents.createBtn')}
              </Button>
              <Button variant="secondary" onClick={() => setDialogOpen(false)} data-testid="event-cancel-btn">
                {t('tools.personalEvents.cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={!!qrEvent} onOpenChange={(o) => !o && setQrEvent(null)}>
        <DialogContent className="max-w-sm" data-testid="event-qr-dialog">
          <DialogHeader>
            <DialogTitle>{qrEvent?.name}</DialogTitle>
            <DialogDescription>{t('tools.personalEvents.scanToPay')}</DialogDescription>
          </DialogHeader>
          {qrEvent && (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="bg-white p-4 rounded-xl border">
                <QRCodeSVG value={qrEvent.payment_url} size={200} data-testid="event-qr-svg" />
              </div>
              <p className="text-sm font-mono text-gray-500">{qrEvent.display_paymig_id}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => copyLink(qrEvent)} data-testid="qr-copy-link-btn">
                  <Copy className="w-4 h-4 mr-1" /> {t('tools.personalEvents.copyLink')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => shareLink(qrEvent)} data-testid="qr-share-btn">
                  <Share2 className="w-4 h-4 mr-1" /> {t('tools.personalEvents.share')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payments dialog */}
      <Dialog open={!!paymentsEvent} onOpenChange={(o) => !o && setPaymentsEvent(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="event-payments-dialog">
          <DialogHeader>
            <DialogTitle>{paymentsEvent?.name} — {t('tools.personalEvents.payments')}</DialogTitle>
            <DialogDescription>{t('tools.personalEvents.received')}: {paymentsEvent?.total_received} SEK</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {payments.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">{t('tools.personalEvents.noPayments')}</p>
            )}
            {payments.map(p => (
              <div key={p.id} className="flex justify-between items-center p-3 border rounded-lg" data-testid={`payment-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{p.payer_name}</div>
                  <div className="text-xs text-gray-400">
                    {p.paid_at ? new Date(p.paid_at).toLocaleString() : ''}
                  </div>
                </div>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 shrink-0">
                  +{p.amount} {p.currency}
                </Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
